import { Suspense } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import AdminLayout from 'components/b2b/AdminLayout';
import AppLayout from 'components/b2b/Layout';
import Loader from 'components/shared/Loader';
import { PATHS, DIRECTORIES } from 'constants/pathsB2b';
import { DesktopPlayer } from 'pages/b2b/Player/DesktopPlayer';
import { lazyWithRetry } from 'services/helpers/lazyWithRetry';
import { withAuthentication } from 'services/hoc/b2b/withAuthentication';

import { EffectsPage } from 'pages/b2b/Effects/EffectsPage';
import { useSelector } from 'react-redux';
import { userTypeSelector } from '@redux/user/selectors';
import { B2bUserType } from 'shared-types/user';
import { DoubleColumnList } from 'components/b2b/DoubleColumnList/DoubleColumnList';

const Playlist = lazyWithRetry(() => import('./pages/b2b/Playlist'));
const Home = lazyWithRetry(() => import('./pages/b2b/Home'));
const Player = lazyWithRetry(() => import('./pages/b2b/Player'));
const Session = lazyWithRetry(() => import('./pages/b2b/Session'));
const Settings = lazyWithRetry(() => import('./pages/b2b/Settings'));
const SessionEnd = lazyWithRetry(() => import('./pages/b2b/SessionEnd'));
const PlayerSessionEnd = lazyWithRetry(() => import('./pages/b2b/PlayerSessionEnd'));
const Catalog = lazyWithRetry(() => import('./pages/b2b/Catalog'));
const ClientCode = lazyWithRetry(() => import('./components/b2b/ClientCode/ClientCode'));
const Light = lazyWithRetry(() => import('./pages/b2b/Light'));
const BamStaff = lazyWithRetry(() => import('./pages/b2b/Staff/bam/BamStaff'));
const BamPlaylistGrid = lazyWithRetry(
  () => import('./pages/b2b/Playlist/components/BamPlaylistGrid'),
);
const CustomPlaylist = lazyWithRetry(
  () => import('./pages/b2b/Playlist/components/CustomPlaylist'),
);
const Staff = lazyWithRetry(() => import('./pages/b2b/Staff'));

const declaredRoutes = () => {
  const userType = useSelector(userTypeSelector);

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={PATHS.PLAYER}
          element={
            <Suspense fallback={<Loader />}>
              <Player />
            </Suspense>
          }
        />
        <Route path={PATHS.DESKTOP_PLAYER} element={<DesktopPlayer />} />
        <Route
          path={PATHS.SESSION}
          element={
            <AdminLayout>
              <Session />
            </AdminLayout>
          }
        />
        <Route
          path={PATHS.STAFF}
          element={
            <AdminLayout>
              <Suspense fallback={<Loader />}>
                {userType === B2bUserType.BAM ? <BamStaff /> : <Staff />}
              </Suspense>
            </AdminLayout>
          }
        />
        <Route
          path={PATHS.HOME}
          element={
            <AppLayout>
              <Home />
            </AppLayout>
          }
        />
        {[PATHS.PLAYLIST, PATHS.GENRE].map((path: string) => (
          <Route
            key={path}
            path={path}
            element={
              <AppLayout>
                <Suspense fallback={<Loader />}>
                  <Playlist />
                </Suspense>
              </AppLayout>
            }
          />
        ))}
        {[DIRECTORIES.PLAYLIST, DIRECTORIES.GENRE, DIRECTORIES.SEE_ALL].map((path: string) => (
          <Route
            key={path}
            path={path}
            element={
              <AppLayout>
                <BamPlaylistGrid />
              </AppLayout>
            }
          />
        ))}
        <Route
          key={PATHS.SONG_LIST}
          path={PATHS.SONG_LIST}
          element={
            <AppLayout>
              <Suspense fallback={<Loader />}>
                <DoubleColumnList />
              </Suspense>
            </AppLayout>
          }
        />
        <Route
          path={PATHS.CUSTOM_PLAYLIST}
          element={
            <AppLayout>
              <CustomPlaylist />
            </AppLayout>
          }
        />
        <Route
          path={PATHS.CLIENT_CODE}
          element={
            <AdminLayout>
              <Suspense fallback={<Loader />}>
                <ClientCode />
              </Suspense>
            </AdminLayout>
          }
        />
        <Route
          path={PATHS.LIGHTS}
          element={
            <AppLayout>
              <Suspense fallback={<Loader />}>
                <Light />
              </Suspense>
            </AppLayout>
          }
        />
        <Route
          path={PATHS.EFFECTS}
          element={
            <AppLayout>
              <Suspense fallback={<Loader />}>
                <EffectsPage />
              </Suspense>
            </AppLayout>
          }
        />
        <Route
          path={PATHS.CATALOG}
          element={
            <Suspense fallback={<Loader />}>
              <Catalog />
            </Suspense>
          }
        />
        <Route
          path={PATHS.SETTINGS}
          element={
            <Suspense fallback={<Loader />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          path={PATHS.SESSION_END}
          element={
            <Suspense fallback={<Loader />}>
              <SessionEnd onLogoLongPress={() => void 0} />
            </Suspense>
          }
        />
        <Route
          path={PATHS.PLAYER_SESSION_END}
          element={
            <Suspense fallback={<Loader />}>
              <PlayerSessionEnd />
            </Suspense>
          }
        />
        <Route
          path='*'
          element={
            <Suspense fallback={<Loader />}>
              <Navigate to={PATHS.STAFF} replace />
            </Suspense>
          }
        />
      </Routes>
    </Suspense>
  );
};

export const B2bRoutes = withAuthentication(declaredRoutes);
